import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Percent,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Copy,
  RefreshCw,
  LogOut,
  Tag,
  Gift,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  collection, 
  getDocs, 
  deleteDoc, 
  updateDoc,
  doc,
  query,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';
import AddPromotionForm from '../../components/admin/AddPromotionForm';

const PromotionsManagement = () => {
  const navigate = useNavigate();
  
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, discount, coupon
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive, expired
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      console.log('🔄 Fetching promotions...');
      
      // Fetch both discounts and coupons
      const [discountsSnapshot, couponsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'discounts'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'coupons'), orderBy('createdAt', 'desc')))
      ]);

      const discounts = discountsSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'discount',
        ...doc.data()
      }));

      const coupons = couponsSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'coupon',
        ...doc.data()
      }));

      const allPromotions = [...discounts, ...coupons];
      setPromotions(allPromotions);
      console.log('✅ Fetched promotions:', allPromotions.length);
    } catch (error) {
      console.error('❌ Error fetching promotions:', error);
      toast.error('Failed to fetch promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promotion) => {
    console.log('📝 Editing promotion:', promotion);
    setEditingPromotion(promotion);
    setShowAddForm(true);
  };

  const handleDelete = async (promotion) => {
    if (window.confirm(`Are you sure you want to delete this ${promotion.type}?`)) {
      try {
        const collectionName = promotion.type === 'discount' ? 'discounts' : 'coupons';
        await deleteDoc(doc(db, collectionName, promotion.id));
        toast.success(`${promotion.type.charAt(0).toUpperCase() + promotion.type.slice(1)} deleted successfully!`);
        fetchPromotions();
      } catch (error) {
        console.error('Error deleting promotion:', error);
        toast.error('Failed to delete promotion');
      }
    }
  };

  const togglePromotionStatus = async (promotion) => {
    try {
      const collectionName = promotion.type === 'discount' ? 'discounts' : 'coupons';
      await updateDoc(doc(db, collectionName, promotion.id), {
        isActive: !promotion.isActive,
        updatedAt: new Date()
      });
      
      setPromotions(prevPromotions =>
        prevPromotions.map(p =>
          p.id === promotion.id ? { ...p, isActive: !promotion.isActive } : p
        )
      );
      
      toast.success(`${promotion.type.charAt(0).toUpperCase() + promotion.type.slice(1)} ${!promotion.isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating promotion status:', error);
      toast.error('Failed to update promotion status');
    }
  };

  const copyPromotionCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  const generatePromotionCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleFormSuccess = () => {
    console.log('🎉 Form submission successful, refreshing promotions...');
    fetchPromotions();
  };

  const handleFormClose = () => {
    console.log('🚪 Closing form...');
    setShowAddForm(false);
    setEditingPromotion(null);
  };

  const getStatusColor = (promotion) => {
    if (!promotion.isActive) return '#dc2626';
    
    const now = new Date();
    const validFrom = new Date(promotion.validFrom);
    const validUntil = new Date(promotion.validUntil);
    
    if (now < validFrom) return '#f59e0b'; // Not started
    if (now > validUntil) return '#dc2626'; // Expired
    return '#059669'; // Active
  };

  const getStatusText = (promotion) => {
    if (!promotion.isActive) return 'Inactive';
    
    const now = new Date();
    const validFrom = new Date(promotion.validFrom);
    const validUntil = new Date(promotion.validUntil);
    
    if (now < validFrom) return 'Not Started';
    if (now > validUntil) return 'Expired';
    return 'Active';
  };

  const filteredPromotions = useMemo(() => {
    let filtered = promotions;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(promotion => 
        promotion.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promotion.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promotion.code?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(promotion => promotion.type === filterType);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(promotion => {
        if (filterStatus === 'active') return promotion.isActive && getStatusText(promotion) === 'Active';
        if (filterStatus === 'inactive') return !promotion.isActive;
        if (filterStatus === 'expired') return getStatusText(promotion) === 'Expired';
        return true;
      });
    }

    return filtered;
  }, [promotions, searchQuery, filterType, filterStatus]);

  const totalPages = Math.ceil(filteredPromotions.length / itemsPerPage);
  const currentPromotions = filteredPromotions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const PromotionCard = React.memo(({ promotion }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease',
      position: 'relative'
    }}>
      {/* Type Badge */}
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        backgroundColor: promotion.type === 'discount' ? '#dbeafe' : '#fef3c7',
        color: promotion.type === 'discount' ? '#1e40af' : '#d97706',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {promotion.type === 'discount' ? <Percent style={{ width: '12px', height: '12px' }} /> : <Tag style={{ width: '12px', height: '12px' }} />}
        {promotion.type === 'discount' ? 'Discount' : 'Coupon'}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '16px',
        marginRight: '80px'
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            {promotion.name}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 12px 0',
            lineHeight: '1.4'
          }}>
            {promotion.description}
          </p>
          
          {/* Code for coupons */}
          {promotion.type === 'coupon' && promotion.code && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <span style={{
                backgroundColor: '#f3f4f6',
                color: '#1f2937',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'monospace'
              }}>
                {promotion.code}
              </span>
              <button
                onClick={() => copyPromotionCode(promotion.code)}
                style={{
                  padding: '6px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Copy style={{ width: '14px', height: '14px' }} />
              </button>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <span style={{
              backgroundColor: `${getStatusColor(promotion)}20`,
              color: getStatusColor(promotion),
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {getStatusText(promotion)}
            </span>
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <DollarSign style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>
              {promotion.discountType === 'percentage' ? 'Discount' : 'Amount'}
            </p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {promotion.discountType === 'percentage' ? `${promotion.discountValue}%` : `₹${promotion.discountValue}`}
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <TrendingUp style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Min Order</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              ₹{promotion.minOrderAmount || promotion.minOrder}
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <Calendar style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Valid Until</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {new Date(promotion.validUntil).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <Users style={{ width: '16px', height: '16px', color: '#ff6b35' }} />
          <div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 2px 0' }}>Usage Limit</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
              {promotion.usageLimit || 'Unlimited'}
            </p>
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          <Clock style={{ width: '14px', height: '14px' }} />
          <span>Created: {new Date(promotion.createdAt?.toDate?.() || promotion.createdAt).toLocaleDateString()}</span>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '8px'
        }}>
          <button
            onClick={() => togglePromotionStatus(promotion)}
            style={{
              padding: '8px',
              backgroundColor: promotion.isActive ? '#fee2e2' : '#dcfce7',
              color: promotion.isActive ? '#dc2626' : '#059669',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {promotion.isActive ? <XCircle style={{ width: '16px', height: '16px' }} /> : <CheckCircle style={{ width: '16px', height: '16px' }} />}
          </button>
          <button
            onClick={() => handleEdit(promotion)}
            style={{
              padding: '8px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Edit style={{ width: '16px', height: '16px' }} />
          </button>
          <button
            onClick={() => handleDelete(promotion)}
            style={{
              padding: '8px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Trash2 style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>
    </div>
  ));

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>Loading promotions...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/admin')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1.5rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>Promotions Management</h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Manage discounts & coupons</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => {
                  console.log('🔄 Refreshing promotions...');
                  fetchPromotions();
                }}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <RefreshCw style={{ width: '16px', height: '16px' }} />
                Refresh
              </Button>
              <Button
                onClick={() => {
                  console.log('➕ Opening add promotion form...');
                  setEditingPromotion(null);
                  setShowAddForm(true);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Plus style={{ width: '16px', height: '16px' }} />
                Add Promotion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 4px 0' }}>
              {promotions.length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Total Promotions</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#059669', margin: '0 0 4px 0' }}>
              {promotions.filter(p => p.isActive && getStatusText(p) === 'Active').length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Active Promotions</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1e40af', margin: '0 0 4px 0' }}>
              {promotions.filter(p => p.type === 'discount').length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Discounts</p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#d97706', margin: '0 0 4px 0' }}>
              {promotions.filter(p => p.type === 'coupon').length}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Coupons</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{ position: 'relative' }}>
              <Search style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: '#6b7280'
              }} />
              <input
                type="text"
                placeholder="Search promotions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Types</option>
              <option value="discount">Discounts</option>
              <option value="coupon">Coupons</option>
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {/* Promotions Grid */}
        <div style={{
          display: 'grid',
          gap: '20px'
        }}>
          {currentPromotions.length === 0 ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '32px'
              }}>
                🎯
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
                No promotions found
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                Add your first promotion to get started
              </p>
            </div>
          ) : (
            currentPromotions.map((promotion) => (
              <PromotionCard key={`${promotion.type}-${promotion.id}`} promotion={promotion} />
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            marginTop: '32px'
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === page ? '#ff6b35' : 'white',
                  color: currentPage === page ? 'white' : '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showAddForm && (
        <AddPromotionForm
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          editingPromotion={editingPromotion}
        />
      )}
    </div>
  );
};

export default PromotionsManagement;





